import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/solid";
import { find, isEmpty } from "lodash";
import { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import ContactComponent from "../../../components/ContactComponent";
import { getLabelColorStyle } from "../../../components/labels/utils";
import { useAppearance } from "../../../context/appearance";
import {
  getCommissionReportTitle,
  useCommissionReports
} from "../../../context/commissionReports";
import ContactCommissionsList from "../../../context/commissionReports/ContactCommissionsList";
import { useContacts } from "../../../context/contacts";
import { getPaymentContextTitle } from "../../../context/contacts/ContactsProvider";
import { formatDateToLocale } from "../../../utils/dates";
import {
  CommissionReport,
  ContactCommission,
  ContactPayment
} from "../../../utils/types";
import useCurrency, { CurrencyKey } from "../../../utils/useCurrency";
import { useHntMarket } from "../../../utils/useHntMarket";
import MemberCell from "./MemberCell";

interface Props {
  contactPayment: ContactPayment | null;
  open: boolean;
  onClose: () => void;
}

const ContactPaymentSidebar: React.FC<Props> = ({
  contactPayment,
  open,
  onClose
}) => {
  const { contacts } = useContacts();
  const { displayInCurrency } = useHntMarket();
  const { getCurrencyTitle, currencyDisplay } = useCurrency();
  const { commissionReports } = useCommissionReports();
  const { isDark } = useAppearance();

  const renderSubtitle = useMemo(() => {
    if (!contactPayment) return null;

    return (
      <div className="flex items-center flex-wrap">
        <div
          className="inline-flex items-center rounded-full font-medium px-3 text-sm mt-2"
          style={getLabelColorStyle("#d73aaa", isDark)}
        >
          {getCurrencyTitle(contactPayment.currency)}
        </div>
      </div>
    );
  }, [contactPayment, getCurrencyTitle, isDark]);

  const [commissionReport, commissions]: [
    CommissionReport | undefined,
    ContactCommission[]
  ] = useMemo(() => {
    const commissionReport = contactPayment?.report_commission_uuid
      ? commissionReports[contactPayment.report_commission_uuid]
      : undefined;

    const commissions =
      commissionReport && contactPayment
        ? find(
            commissionReport.report,
            ({ contact }) => contact.uuid === contactPayment.contact_uuid
          )?.commissions
        : undefined;

    return [commissionReport, commissions || []];
  }, [commissionReports, contactPayment]);

  const renderContent = useMemo(() => {
    if (!contactPayment) return null;

    const currentContact = contacts[contactPayment.contact_uuid];

    return (
      <div className="flex-1 py-6 divide-y divide-gray-200 dark:divide-gray-500 overflow-y-auto">
        <div className="px-4 sm:px-6 space-y-6">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                Paid amount
              </dt>

              <dd className="flex items-baseline mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                {contactPayment.currency === CurrencyKey.hnt
                  ? displayInCurrency(contactPayment.amount, {
                      withHntSuffix: true,
                      needsDivision: true,
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3
                    })
                  : currencyDisplay(
                      contactPayment.currency,
                      contactPayment.amount
                    )}
              </dd>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-medium text-gray-500">Added by</h3>
            <MemberCell
              memberUuid={contactPayment.created_by}
              subtitle={formatDateToLocale(contactPayment.created_at)}
            />
          </div>

          <div>
            <h3 className="mb-4 text-sm font-medium text-gray-500">
              Paid contact
            </h3>
            {currentContact ? (
              <Link
                className="relative flex items-center space-x-3 group"
                to={`/contacts/${contactPayment.contact_uuid}`}
                onClick={onClose}
              >
                <ContactComponent contactId={contactPayment.contact_uuid} />
              </Link>
            ) : (
              <div className="relative flex items-center space-x-3">
                <ContactComponent contactId={contactPayment.contact_uuid} />
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">Payment date</h3>
            <div className="mt-2">
              {contactPayment.paid_at
                ? formatDateToLocale(contactPayment.paid_at)
                : "-"}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">Context</h3>
            <div className="mt-2">
              {contactPayment.context
                ? getPaymentContextTitle(contactPayment.context)
                : "-"}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">Notes</h3>
            <div className="mt-2">
              {contactPayment.notes ? (
                <pre className="text-sm">{contactPayment.notes}</pre>
              ) : (
                "-"
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">
              Commission report
            </h3>
            <div className="mt-2">
              {contactPayment.report_commission_uuid ? (
                <Link
                  className="text-sm hover:text-gray-600 truncate font-semibold dark:hover:text-white dark:hover:underline"
                  to={`/workspace/commission-reports/${contactPayment.report_commission_uuid}`}
                  onClick={onClose}
                >
                  {commissionReport
                    ? getCommissionReportTitle(commissionReport)
                    : "Commission report"}
                </Link>
              ) : (
                "-"
              )}
            </div>
          </div>

          {commissionReport && !isEmpty(commissions) && (
            <div>
              <ContactCommissionsList
                commissionReport={commissionReport}
                commissions={commissions}
                closeSidebar={onClose}
              />
            </div>
          )}
        </div>
      </div>
    );
  }, [
    commissionReport,
    commissions,
    contactPayment,
    contacts,
    currencyDisplay,
    displayInCurrency,
    onClose
  ]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={open}
        onClose={onClose}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <div className="absolute inset-0" onClick={onClose} />
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-md dark:text-white">
                <div className="max-w-3xl xl:max-w-6xl h-full">
                  <div className="h-full flex flex-col bg-white shadow-xl dark:bg-gray-900">
                    <div className="flex-1 h-0 flex flex-col">
                      <div className="flex-none py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-lg font-medium text-white">
                            Contact payment
                            {renderSubtitle}
                          </Dialog.Title>
                          <div className="ml-3 h-7 flex items-center">
                            <button
                              type="button"
                              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                              onClick={onClose}
                            >
                              <span className="sr-only">Close panel</span>
                              <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {renderContent}
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ContactPaymentSidebar;
